/**
 * Product component
 *
 * Usage:
 *
 *   <div x-data="product('{{ product.shopifyId }}', '{{ product.url }}')">
 *     ...
 *     <button x-on:click="addLineItem('{{ product.getDefaultVariant().id }}')">Add to Cart</button>
 *   </div>
 *
 * @param id
 * @param url
 * @returns {{init(): Promise<void>, product: null, addLineItem(*, number=): Promise<void>, id, url: string}}
 */
export default function(id, url = '', variants = [], currencyPricingKey = 'gbContextualPricing', slides = []) {
  let base = 'gid://shopify/ProductVariant/';

  return {
    /**
     * ID of product
     */
    id: id,

    /**
     * Memoized copy of product
     */
    product: null,

    /**
     * Url to product page in Craft
     */
    url: url,

    /**
     * Internal flag indicating that an update is underway
     */
    updating: false,

    /**
     * Array of the product's variant options
     */
    variantOptions: null,

    /**
     * Array of product variants
     */
    variants: null,

    /**
     * The currently selected variant's ID
     */
    _currentVariantId: null,

    /**
     * Can the product be added to the cart?
     */
    canAddToCart: false,

    /**
     * The selected 'add to cart' quantity for the current variant
     */
    qty: 1,

    /**
     * The max available quantity of the current variant
     */
    maxQty: 1,

    /**
     * ID of the selected product image
     */
    selectedImg: null,

    variantSelected: false,

    slides: null,

    activeSlide: 0,

    /**
     * Component init, fetches product details from API
     *
     * @returns {Promise<void>}
     */
    init() {
      this.currentVariantId = id;
      this.slides = slides;
      if(!this.$refs.variantForm) return;

      this.variantOptions = this.$refs.variantForm.querySelectorAll("[data-option]");

      this.$refs.variantForm.addEventListener("change", (e) => {
        this.selectVariantOption();
      });

      // Trigger an initial `change` event to simulate a selection:
      this.$refs.variantForm.dispatchEvent(new Event("change"));
    },

    /**
     * Method to handle the scrolling logic.
     * It is called when a thumbnail is clicked.
     * @param {number} index - The index of the slide to navigate to.
     */
    goToSlide(index) {
      this.activeSlide = index;

      const container = this.$refs.slidesContainer;
      const swiperEl = container.querySelector('.swiper');
      if (swiperEl?.swiper) {
        swiperEl.swiper.slideTo(index);
        return;
      }

      const imageOffset = document.getElementById('image-' + index).offsetTop;
      const scrollPosition = imageOffset + container.offsetTop - 20;
      window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    },

    /**
     *
     *
     * @returns void
     */
    selectVariantOption() {
      const selectedOptions = {};

      this.variantOptions.forEach((input) => {
        selectedOptions[input.dataset.option] = input.value;
      });

      const variant = this.findVariant(selectedOptions);

      if (!variant) {
        console.warn("No variant exists for options:", selectedOptions);
        this.currentVariantId = null;
        this.canAddToCart = false;
        return;
      }

      this.currentVariantId = variant.shopifyId;

      const variantData = variant.data ?? {};
      const pricingData = variantData[currencyPricingKey];

      if (variantData.image) {
        this.selectedImg = variantData.image.url;
        if (this.variantSelected) {
          const imgBase = variantData.image.url.split('?')[0];
          const slideIndex = slides.findIndex(s => s.src && s.src.split('?')[0] === imgBase);
          if (slideIndex >= 0) {
            this.goToSlide(slideIndex);
          }
        }
      }

      const inventoryQty = variantData.inventoryQuantity ?? 0;
      this.maxQty = inventoryQty < 0 ? 999 : inventoryQty;
      if (this.qty > this.maxQty) {
        this.qty = Math.max(this.maxQty, 1);
      }
      this.canAddToCart = inventoryQty !== 0;

      if (pricingData?.price) {
        let amount = pricingData.price.amount;
        let minimumFractionDigits = amount % 1 === 0 ? 0 : 2;
        let price = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: pricingData.price.currencyCode,
          minimumFractionDigits: minimumFractionDigits,
          maximumFractionDigits: 2,
        }).format(amount);
        this.$refs.productPrice.innerText = price;
      }

      const searchUrl = new URL(location.href);
      const params = new URLSearchParams(searchUrl.search);

      params.set("variantId", this.currentVariantId);

      up.reload({
        url: `${location.pathname}?${params}`,
        target: '#product-price',
        history: this.variantSelected
      });

      this.variantSelected = true;
    },

    findVariant(options) {
      variant: for (const v in variants) {
        option: for (const o in options) {
          if (variants[v].data?.selectedOptions?.[o-1]?.value !== options[o]) {
            continue variant;
          }
        }
        return variants[v];
      }
    },

    decQty() {
      if(this.qty <= 1) return;
      this.qty = this.qty - 1;
    },

    incQty() {
      if(this.qty >= this.maxQty) return;
      this.qty = this.qty + 1;
    },

    get currentVariantId() {
      return this._currentVariantId;
    },

    set currentVariantId(id) {
      this._currentVariantId = id;
    },

    /**
     * Add variant to cart
     * Supports passing id or admin_graphql_api_id
     *
     * @param variantId
     * @param qty
     * @returns {Promise<void>}
     */
    async addLineItem() {
      if(!this.currentVariantId) return;

      const variantId = String(this.currentVariantId);
      let gid = variantId.startsWith(base) ? variantId : base + variantId;
      this.updating = true;
      await Alpine.store('cart').addLineItem(
        gid,
        Number(this.qty),
        [
          {
            key: '_productUrl',
            value : this.url,
          }
        ]
      );
      console.log('Added ' + variantId);
      this.updating = false;
    }
  };
}
